/**
 * The page-content block model.
 *
 * Split from types.ts because it is the single largest concern in there and
 * has its own shape: a deliberately FLAT record where every field exists on
 * every block regardless of type (see the note on PageContent), plus the
 * field-group and label tables the editor drives from.
 */

// ── Page content blocks ──────────────────────────────────────────────────────

/**
 * The values of `pagecontent.type` this app renders as blocks. The raw table
 * has 13 values including "footer", but footer content is fully modeled via
 * Site.footer now (see FIDELITY NOTE 1b) rather than as a pageContent
 * block, so "footer" is intentionally excluded here — 12 values remain.
 */
export type PageContentType =
  | "about" | "mission" | "channels" | "info" | "services" | "image"
  | "richtext" | "categories" | "sections" | "Contact" | "popup"
  | "text"

/**
 * One block of page content. Every field is optional and flat, because real
 * data does not respect the type-based grouping Directus's UI presents — see
 * FIDELITY NOTE 2 above. Use PAGECONTENT_FIELD_GROUPS to know which fields a
 * given type is actually meant to use when building an editor.
 */
export interface PageContent {
  id: string
  type: PageContentType

  /**
   * An admin-facing name for the block ("A title to identify the block" in the
   * Directus form). Not rendered on the public page and not translated — it
   * exists so a list of blocks is readable, which is exactly what the browser
   * tree needs. Distinct from the per-type content titles like servicesTitle.
   */
  title?: string

  /**
   * Per-block styling — confirmed on a live "channels" block AND a live
   * "footer" block, not just type "text" as this app first assumed. Available
   * on every block type; most just leave them unset.
   */
  bgColor?: string
  textColor?: string

  // group_about — the "about" block's rich HTML body is the real Directus
  // field named "subtitle" (confirmed on a live row), not a separate
  // "description" field; kept as aboutSubtitle here to match.
  aboutTitle?: string
  /** HTML. Directus field name is "subtitle" on this block type. */
  aboutSubtitle?: string
  /** Directus file id (directus_files); stored as an opaque string/url here. */
  aboutImage?: string

  // group_mission
  statementTitle?: string
  missionImage?: string

  // group_channels — each channel has a label and an optional link/handle.
  channelsTitle?: string
  channelsSubtitle?: string
  fb?: string
  fbLink?: string
  fbmess?: string
  fbmessLink?: string
  instagram?: string
  instagramLink?: string
  telegram?: string
  telegramLink?: string
  whatsapp?: string
  whatsappLink?: string
  /** A second WhatsApp channel — Directus's own field name, not a typo. */
  whatsappc?: string
  whatsappcLink?: string
  email?: string
  emailLink?: string
  facetime?: string
  facetimeLink?: string
  /** Directus field name is "toktok" (not "tiktok") for the label; the link field is spelled correctly. */
  toktok?: string
  tiktokLink?: string
  x?: string
  xLink?: string
  youtube?: string
  youtubeLink?: string

  // group_info
  infoTitle?: string
  infoSubtitle?: string

  // group_services
  servicesTitle?: string
  servicesSubtitle?: string
  /**
   * Which service-browsing UI pieces to show or hide.
   *
   * A JSON object of boolean flags. The flag set is now enumerated from the
   * real Directus form (see SERVICES_COMPONENT_FLAGS) rather than left open —
   * an earlier version of this model treated it as opaque because the keys had
   * not been confirmed.
   *
   * Note the polarity: every flag except showIA is a HIDE flag, so absent or
   * false means the piece is VISIBLE. Do not "clean up" a row by setting them
   * all false; that is already the default.
   *
   * Extra keys are preserved rather than typed away — production rows may
   * carry flags this form no longer shows.
   */
  components?: Partial<Record<ServicesComponentFlag, boolean>> & Record<string, any>
  /** The "Service Details" group. Same shape and same hide-polarity. */
  componentsDetails?: Partial<Record<ServicesDetailFlag, boolean>> & Record<string, any>
  textAccessibility?: string
  textPopulations?: string
  textProviders?: string
  textTypeOfService?: string

  // group_richtext
  richtextTitle?: string
  /** HTML. */
  richtext?: string

  // group_categories
  categoriesTitle?: string
  categoriesSubtitle?: string

  // group_sections
  sectionsTitle?: string
  sectionsSubtitle?: string

  // group_contact
  contactName?: string
  contactEmail?: string
  contactSubject?: string
  contactMessage?: string

  // group_popup
  //
  // Confirmed against BlockPopup in signpost-monorepo's lib/types.blocks.ts:
  // a popup carries BOTH its own popup_title/popup_text AND the generic
  // title/subtitle/text, plus a call-to-action button. Only the first two were
  // modelled here before, so the button and the generic fields were
  // unreachable from the editor and unwritable by the agent.
  popupTitle?: string
  /** Plain text, despite the multiline input. */
  popupText?: string
  popupSubtitle?: string
  /** Call-to-action label. */
  buttonText?: string
  /** Call-to-action target. */
  buttonLink?: string

  // group_simpletext / group_block — generic styled text, used by type "text".
  // bgColor/textColor moved to base fields above (confirmed present on other
  // types too); colorBg/colorText are kept as distinct, still-unconfirmed
  // fields from the original schema fetch rather than assumed duplicates.
  text?: string
  textBold?: boolean
  textSize?: number
  fontWeight?: number
  colorBg?: string
  colorText?: string

  // grop_image (Directus's own misspelling of the group name, kept for traceability)
  /** Directus file id. */
  image?: string

  // map_center — the "services" block only; it is the sole type that
  // renders a map. See PAGECONTENT_FIELD_GROUPS.
  latitude?: number
  longitude?: number

  translations: Record<string, TranslatedPageContent>
  oldid?: number
  createdAt: number
  updatedAt: number
}

/**
 * Per-locale text for a PageContent block. Only fields with a real Directus
 * translation table (see FIDELITY NOTE 3) appear here — everything else on
 * PageContent (colors, ids, booleans, coordinates) is not translatable in
 * Directus and stays locale-independent.
 */
export interface TranslatedPageContent {
  locale: string
  // Whether a person has checked this locale's wording, as in the other sections
  reviewed?: boolean
  // pagecontent_translations
  text?: string
  // pagecontent_translations_1
  richtext?: string
  richtextTitle?: string
  // pagecontent_translations_2
  channelsTitle?: string
  channelsSubtitle?: string
  email?: string
  facetime?: string
  fb?: string
  fbmess?: string
  instagram?: string
  telegram?: string
  toktok?: string
  whatsapp?: string
  whatsappc?: string
  x?: string
  youtube?: string
  // pagecontent_translations_4
  servicesTitle?: string
  servicesSubtitle?: string
  textAccessibility?: string
  textPopulations?: string
  textProviders?: string
  textTypeOfService?: string
  // pagecontent_translations_5
  infoTitle?: string
  infoSubtitle?: string
  // pagecontent_translations_7
  categoriesTitle?: string
  categoriesSubtitle?: string
  // pagecontent_translations_8
  sectionsTitle?: string
  sectionsSubtitle?: string
  // pagecontent_translations_9
  statementTitle?: string
  // pagecontent_translations_11
  aboutTitle?: string
  aboutSubtitle?: string
  // pagecontent_translations_12
  contactName?: string
  contactEmail?: string
  contactSubject?: string
  contactMessage?: string
  // pagecontent_translations_13
  popupTitle?: string
  popupText?: string
  popupSubtitle?: string
  buttonText?: string
  buttonLink?: string
  /**
   * Per-locale image overrides. Directus kept a single file reference per row
   * shared by all locales; we're replacing it, and localised artwork (text
   * baked into a banner, region-specific imagery) is a real need. Unset means
   * "inherit the source locale's image", so these stay optional.
   */
  image?: string
  aboutImage?: string
  missionImage?: string
  updatedAt: number
}

/**
 * The `components` flags on a "services" block, taken from the Directus form.
 *
 * All but showIA are HIDE flags: true removes that piece from the rendered
 * service browser. showIA is the one positive flag.
 */
export const SERVICES_COMPONENT_FLAGS = [
  "hidemap", "hidelist", "hidesearch", "hideservicetype",
  "hideaccesibility", "hidepopulation", "hideproviders", "showIA",
] as const
export type ServicesComponentFlag = (typeof SERVICES_COMPONENT_FLAGS)[number]

/** The `componentsDetails` flags — Directus's "Service Details" group. */
export const SERVICES_DETAIL_FLAGS = ["hidelastupdate"] as const
export type ServicesDetailFlag = (typeof SERVICES_DETAIL_FLAGS)[number]

/** Human labels, matching the Directus form's checkbox text. */
export const SERVICES_FLAG_LABELS: Record<ServicesComponentFlag | ServicesDetailFlag, string> = {
  hidemap: "Hide Map",
  hidelist: "Hide List",
  hidesearch: "Hide Search",
  hideservicetype: "Hide Service Type",
  // Directus's own misspelling of "accessibility"; kept because it is the
  // stored key.
  hideaccesibility: "Hide Accessibility",
  hidepopulation: "Hide Populations",
  hideproviders: "Hide Providers",
  showIA: "Show IA",
  hidelastupdate: "Hide Last Update",
}

/**
 * Which PageContent fields Directus's own UI shows for each type — i.e. the
 * fields a block of that type is MEANT to use. Not an enforced constraint:
 * see FIDELITY NOTE 2. Useful for building a type-aware editor that defaults
 * to showing only the relevant fields, while still allowing (and preserving)
 * whatever else a row happens to carry.
 */
/**
 * Styling every block type carries (see PageContent's bgColor/textColor).
 *
 * Spread into every group below rather than listed once: the groups table is
 * what the editor renders from AND what siteconfig_pagecontent_field_guide
 * reports to the agent, so a field missing here is invisible in the form and
 * denied by the guide — even though the model documents it as universal.
 */
const BLOCK_STYLE_FIELDS = [
  "bgColor", "textColor", "textSize", "fontWeight", "text",
] as const satisfies readonly (keyof PageContent)[]

export const PAGECONTENT_FIELD_GROUPS: Record<PageContentType, Array<keyof PageContent>> = {
  about: [...BLOCK_STYLE_FIELDS, "aboutTitle", "aboutSubtitle", "aboutImage"],
  mission: [...BLOCK_STYLE_FIELDS, "statementTitle", "missionImage"],
  channels: [
    ...BLOCK_STYLE_FIELDS,
    "channelsTitle", "channelsSubtitle",
    "fb", "fbLink", "fbmess", "fbmessLink", "instagram", "instagramLink",
    "telegram", "telegramLink", "whatsapp", "whatsappLink", "whatsappc", "whatsappcLink",
    "email", "emailLink", "facetime", "facetimeLink", "toktok", "tiktokLink",
    "x", "xLink", "youtube", "youtubeLink",
  ],
  info: [...BLOCK_STYLE_FIELDS, "infoTitle", "infoSubtitle"],
  services: [
    ...BLOCK_STYLE_FIELDS,
    "servicesTitle", "servicesSubtitle", "components", "componentsDetails",
    "textAccessibility", "textPopulations", "textProviders", "textTypeOfService",
    // The map this block renders is centred here; Directus shows both on the
    // services form and on no other type.
    "latitude", "longitude",
  ],
  image: [...BLOCK_STYLE_FIELDS, "image"],
  richtext: [...BLOCK_STYLE_FIELDS, "richtextTitle", "richtext"],
  categories: [...BLOCK_STYLE_FIELDS, "categoriesTitle", "categoriesSubtitle"],
  sections: [...BLOCK_STYLE_FIELDS, "sectionsTitle", "sectionsSubtitle"],
  Contact: [...BLOCK_STYLE_FIELDS, "contactName", "contactEmail", "contactSubject", "contactMessage"],
  popup: [...BLOCK_STYLE_FIELDS, "popupTitle", "popupSubtitle", "popupText", "buttonText", "buttonLink"],
  text: [...BLOCK_STYLE_FIELDS, "textBold"],
}

export const PAGECONTENT_TYPE_LABELS: Record<PageContentType, string> = {
  about: "About",
  mission: "Mission Statement",
  channels: "Communication Channels",
  info: "Information",
  services: "Services",
  image: "Image",
  richtext: "Rich Text",
  categories: "Categories",
  sections: "Sections",
  Contact: "Contact",
  popup: "Popup",
  text: "Text",
}
